import { sumBy } from 'lodash-es';

const getOutstandingBalanceData = (invoices, invoiceId) => {
  const currentInvoice = invoices.find(
    (invoice) => invoice.invoiceID === invoiceId
  );

  if (!currentInvoice) {
    return '0.00';
  }

  const { invoiceDetails, totalPrice } = currentInvoice;

  if (
    typeof invoiceDetails === 'undefined'
    || (Array.isArray(invoiceDetails) && !invoiceDetails.length)
  ) {
    return '0.00';
  }

  let outstandingBal = '0.00';
  if (!currentInvoice.allPaid) {
    outstandingBal = sumBy(
      invoiceDetails,
      (item) => parseFloat((item.paid || item.suppress) ? 0 : item.totalAmount || 0)
    );
  }

  const outstandingBalanceWithAdditionalCharge = outstandingBal + parseFloat(totalPrice.tax) + parseFloat(totalPrice.shipping) + parseFloat(totalPrice.insurance) + parseFloat(totalPrice.other);
  return outstandingBalanceWithAdditionalCharge.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getInvoiceTotalHeader = (invoices, invoiceId) => {
  const currentInvoice = invoices.find(
    (invoice) => invoice.invoiceID === invoiceId
  );

  if (!currentInvoice) {
    return '0.00';
  }

  const { invoiceDetails, totalPrice } = currentInvoice;

  if (
    typeof invoiceDetails === 'undefined'
    || (Array.isArray(invoiceDetails) && !invoiceDetails.length)
  ) {
    return '0.00';
  }

  let outstandingBal = '0.00';
  if (!currentInvoice.allPaid) {
    outstandingBal = sumBy(
      invoiceDetails,
      (item) => parseFloat((item.paid || item.suppress) ? 0 : item.totalAmount || 0)
    );
  }

  const invoiceTotalWithAdditionalCharge = outstandingBal + parseFloat(totalPrice.tax) + parseFloat(totalPrice.shipping) + parseFloat(totalPrice.insurance) + parseFloat(totalPrice.other);
  return invoiceTotalWithAdditionalCharge.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getTotalPaidHeader = (invoices, invoiceId) => {
  const currentInvoice = invoices.find(
    (invoice) => invoice.invoiceID === invoiceId
  );

  if (!currentInvoice) {
    return '0.00';
  }

  const { invoiceDetails, totalPrice } = currentInvoice;

  if (
    typeof invoiceDetails === 'undefined'
      || (Array.isArray(invoiceDetails) && !invoiceDetails.length)
  ) {
    return '0.00';
  }

  let paidBal = '0.00';
  paidBal = sumBy(
    invoiceDetails,
    (item) => parseFloat((item.paid) ? item.totalAmount - item.priceInfo.tax : 0 || 0)
  );

  if (paidBal === 0) {
    return paidBal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  const invoicePaidTotalWithAdditionalCharge = paidBal + parseFloat(totalPrice.tax);
  return invoicePaidTotalWithAdditionalCharge.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDateString = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const mPart = (date.getMonth() + 1).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
  const dPart = date.getDate().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
  const yPart = date.getFullYear().toString().substring(2);

  return `- ${mPart}/${dPart}/${yPart}`;
};

const getInvoiceDetailStatus = (invoiceSentDateString, paidDateString, onlinePaymentStat, isPaid, suppress = false) => {
  let returnString = 'Not Sent';
  if (suppress) {
    returnString = 'Hidden';
  } else if (onlinePaymentStat === 'a' || (onlinePaymentStat.trim() === '' && isPaid)) {
    returnString = `Paid ${formatDateString(paidDateString)}`;
  } else if (onlinePaymentStat === 'p' || onlinePaymentStat === 'u' || onlinePaymentStat === 'h' || onlinePaymentStat === 'i') {
    returnString = 'Payment Pending';
  } else if (onlinePaymentStat === 'd' || onlinePaymentStat === 'f') {
    returnString = 'Payment Failed';
  } else if (invoiceSentDateString) {
    returnString = `Sent ${formatDateString(invoiceSentDateString)}`;
  }

  return returnString;
};

const totalItemsCount = (allInvoicesDetails) => {
  if (allInvoicesDetails.length) {
    return allInvoicesDetails.length - allInvoicesDetails.filter(item => item.paid || item.suppress).length;
  }
};

const locale = navigator.languages[0] || 'en-US';

const preformatFloat = (float) => {
  float += ''; //force this to be a string
  if (!float) {
    return '';
  }
  const parts = Intl.NumberFormat(locale)
    .formatToParts(1000.1); // use a number that will give a separator and a decimal
  const groupingCharacter = ',';
  const decimalCharacter = parts.find(p => p.type === 'decimal').value;

  // Removes all groupings and forces a period for decimal separator
  return float.split(groupingCharacter).join('').replace(decimalCharacter, '.');
};

const formatCurrencyForLocale = (amount) => {
  const parsedNum = parseFloat(preformatFloat(amount));
  return parsedNum.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const exportCSV = (headers, fileData, fileName) => {
  /*  headers is a comma-delimited string
      fileData is an array - [{1, 2, 3}, {4, 5, 6}, {7. 8. 9}]
      fileName is a string without the .csv extension
      if you need commas in your string for address or currency, you must wrap item in double quotes:
      `"${invoice.totalPrice.currencySymbol} ${invoice.outStandingBalance}"`
  */
  const columnCount = headers.split(',').length;

  //  Set the heders in CSV format
  let text = `${headers},\r\n`;

  //  parse the file data to CSV format
  fileData.forEach((row) => {
    row.forEach((item, index) => {
      text += item;
      if (index < (columnCount - 1)) text += ',';
    });
    text += '\r\n';
  });

  //  this creates the pseudo element and opens a dialog box to save or open file
  const element = document.createElement('a');
  element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`);
  element.setAttribute('download', `${fileName}.csv`);
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

const allVisibleArePaid = (invoice) => {
  const allSuppressed = invoice.invoiceDetails.every(item => item.suppress);
  const returnFlag = invoice.invoiceDetails.every(item => item.paid || item.suppress) && !allSuppressed;
  return returnFlag;
};

const padWithZero = (digit) => (digit < 10 ? `0${digit}` : digit);

const formatDate = (theDate) => {
  let returnVal = '';
  if (theDate !== null && theDate !== '' && theDate !== undefined) {
    returnVal = `${padWithZero(theDate.getMonth() + 1)}/${padWithZero(theDate.getDate())}/${padWithZero(theDate.getFullYear())}`;
  }
  return returnVal;
};

export {
  getOutstandingBalanceData,
  getInvoiceTotalHeader,
  getTotalPaidHeader,
  getInvoiceDetailStatus,
  totalItemsCount,
  preformatFloat,
  locale,
  formatCurrencyForLocale,
  formatDate,
  formatDateString,
  exportCSV,
  allVisibleArePaid,
};
